import { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { attendancesApi } from '@/services/api';
import { downloadCSV, readCSVFile, importCSV } from '@/utils/csv';
import { HiOutlineCalendar, HiOutlineCheck, HiOutlineX, HiOutlineDownload, HiOutlineUpload } from 'react-icons/hi';

interface Client {
  id: string;
  lastName: string;
  firstName: string;
  clientNumber: string;
}

interface Attendance {
  id: string;
  clientId: string;
  attendanceDate: string;
  checkInTime: string;
  checkOutTime: string;
  status: string;
  mealProvided: boolean;
  pickupService: boolean;
  dropoffService: boolean;
}

interface AttendanceData {
  attendance: Attendance;
  client: Client;
}

const statusLabels: Record<string, { label: string; class: string }> = {
  present: { label: '出席', class: 'badge-success' },
  absent: { label: '欠席', class: 'badge-danger' },
  late: { label: '遅刻', class: 'badge-warning' },
  early_leave: { label: '早退', class: 'badge-warning' },
  half_day: { label: '半日', class: 'badge-primary' },
  holiday: { label: '休日', class: 'badge-gray' },
};

export default function AttendancePage() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ data: AttendanceData[] }>({
    queryKey: ['attendances', { date }],
    queryFn: () => attendancesApi.list({ date }) as Promise<{ data: AttendanceData[] }>,
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // 月単位でエクスポート
      const [year, month] = date.split('-');
      const startDate = `${year}-${month}-01`;
      const endDate = new Date(parseInt(year), parseInt(month), 0).toISOString().split('T')[0];
      await downloadCSV(`/api/v1/export/attendances?startDate=${startDate}&endDate=${endDate}`);
      toast.success('CSVをダウンロードしました');
    } catch (error) {
      toast.error('ダウンロードに失敗しました');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const csvText = await readCSVFile(file);
      const result = await importCSV('/api/v1/import/attendances', csvText);
      
      if (result.errors?.length > 0) {
        toast.error(`${result.imported}件インポート、${result.errors.length}件エラー`);
        console.error('Import errors:', result.errors);
      } else {
        toast.success(`${result.imported}件インポートしました`);
      }
      
      queryClient.invalidateQueries({ queryKey: ['attendances'] });
    } catch (error) {
      toast.error('インポートに失敗しました');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const attendances = data?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">出欠管理</h1>
          <p className="text-gray-600 mt-1">利用者の出欠を記録します</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleExport}
            disabled={isExporting}
            className="btn-secondary"
          >
            <HiOutlineDownload className="w-5 h-5 mr-2" />
            {isExporting ? 'エクスポート中...' : '月間CSVエクスポート'}
          </button>
          <button 
            onClick={handleImportClick}
            disabled={isImporting}
            className="btn-secondary"
          >
            <HiOutlineUpload className="w-5 h-5 mr-2" />
            {isImporting ? 'インポート中...' : 'CSVインポート'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-4">
            <HiOutlineCalendar className="w-5 h-5 text-gray-400" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input max-w-xs"
            />
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>利用者番号</th>
                <th>氏名</th>
                <th>状態</th>
                <th>入所</th>
                <th>退所</th>
                <th>食事</th>
                <th>送迎(往)</th>
                <th>送迎(復)</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">
                    読み込み中...
                  </td>
                </tr>
              ) : attendances.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">
                    この日の出欠記録はありません
                  </td>
                </tr>
              ) : (
                attendances.map(({ attendance, client }) => (
                  <tr key={attendance.id}>
                    <td className="font-mono text-sm">{client?.clientNumber}</td>
                    <td className="font-medium">
                      {client?.lastName} {client?.firstName}
                    </td>
                    <td>
                      <span className={statusLabels[attendance.status]?.class || 'badge-gray'}>
                        {statusLabels[attendance.status]?.label || attendance.status}
                      </span>
                    </td>
                    <td>{attendance.checkInTime || '-'}</td>
                    <td>{attendance.checkOutTime || '-'}</td>
                    <td className="text-center">
                      {attendance.mealProvided ? (
                        <HiOutlineCheck className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <HiOutlineX className="w-5 h-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                    <td className="text-center">
                      {attendance.pickupService ? (
                        <HiOutlineCheck className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <HiOutlineX className="w-5 h-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                    <td className="text-center">
                      {attendance.dropoffService ? (
                        <HiOutlineCheck className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <HiOutlineX className="w-5 h-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
